import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaChevronDown,
  FaExclamationTriangle,
  FaFilter,
  FaPhoneAlt,
  FaSearch,
  FaSpinner,
  FaTimes,
  FaUsers,
} from "react-icons/fa";
import ActionPageLayout from "../layouts/ActionPageLayout";
import usePageTitle from "../hooks/usePageTitle";
import {
  assignDailyAssignments,
  fetchDailyAssignmentMetadata,
  searchDailyAssignments,
} from "../services/dailyCallAssignmentApi";
import {
  areDailyAssignmentFiltersEqual,
  buildDailyAssignmentPayload,
  buildDailyAssignmentSearchPayload,
  calculateSelectedTarget,
  cloneDailyAssignmentFilters,
  createEmptyDailyAssignmentFilters,
  getDailyAssignmentAgentId,
  getDailyAssignmentRowSignature,
  hasCompleteDailyAssignmentFilters,
  validateDailyAssignmentTarget,
} from "../utils/dailyCallAssignment";

const columns = [
  ["Call Banding", "call_banding", "text-left"],
  ["No attempt 0-7", "no_attempt_0_7", "text-center"],
  ["No attempt 8-15", "no_attempt_8_15", "text-center"],
  ["No attempt 16-30", "no_attempt_16_30", "text-center"],
  ["No attempt 31-60", "no_attempt_31_60", "text-center"],
  ["No attempt 61-90", "no_attempt_61_90", "text-center"],
  ["No. of leads", "available_leads", "text-center"],
];

const filterFields = [
  ["cluster_cities", "Cluster city", "Select cities"],
  ["offer_amount_bands", "Offer amount band", "Select offer bands"],
  ["bureau_bands", "Bureau band", "Select bureau bands"],
  ["pil_vintage_bands", "PIL vintage band", "Select vintages"],
];

function CheckboxDropdown({
  id,
  label,
  options,
  placeholder,
  selectedValues,
  onChange,
  disabled = false,
  searchable = false,
  getOptionLabel = (option) => option,
  getOptionValue = (option) => option,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionValues = options.map(getOptionValue);
  const selectedLabels = options
    .filter((option) => selectedValues.includes(getOptionValue(option)))
    .map(getOptionLabel);
  const allSelected =
    optionValues.length > 0 && selectedValues.length === optionValues.length;
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  const visibleOptions = normalizedSearchTerm
    ? options.filter((option) =>
        String(getOptionLabel(option))
          .toLocaleLowerCase()
          .includes(normalizedSearchTerm),
      )
    : options;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!dropdownRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (isOpen && searchable) {
      searchInputRef.current?.focus();
    } else if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen, searchable]);

  const toggleOption = (optionValue) => {
    onChange(
      selectedValues.includes(optionValue)
        ? selectedValues.filter((value) => value !== optionValue)
        : [...selectedValues, optionValue],
    );
  };

  return (
    <div className="relative min-w-0" ref={dropdownRef}>
      <span
        className="mb-1.5 block text-sm font-semibold text-[#334155]"
        id={`${id}-label`}
      >
        {label}
      </span>
      <button
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby={`${id}-label ${id}-value`}
        className="flex h-[40px] w-full items-center justify-between gap-2 rounded-md border border-[#cbd5e1] bg-white px-3 text-left text-sm text-[#111827] outline-none transition hover:border-[#94a3b8] focus:border-[#0d73ff] focus:shadow-[0_0_0_3px_rgba(13,115,255,0.15)] disabled:cursor-not-allowed disabled:bg-[#f1f5f9] disabled:text-[#94a3b8]"
        disabled={disabled}
        id={id}
        type="button"
        onClick={() => setIsOpen((current) => !current)}
      >
        <span
          className="truncate"
          id={`${id}-value`}
          title={selectedLabels.join(", ")}
        >
          {selectedLabels.length > 0 ? selectedLabels.join(", ") : placeholder}
        </span>
        <FaChevronDown
          aria-hidden="true"
          className={`h-3 w-3 flex-none text-[#475569] transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen ? (
        <div
          aria-labelledby={`${id}-label`}
          aria-multiselectable="true"
          className="absolute inset-x-0 top-[calc(100%+0.25rem)] z-40 max-h-64 overflow-y-auto rounded-md border border-[#cbd5e1] bg-white p-1.5 shadow-[0_10px_25px_rgba(15,23,42,0.2)]"
          role="listbox"
        >
          {searchable ? (
            <div className="sticky top-0 z-10 bg-white p-1">
              <input
                aria-label={`Search ${label}`}
                autoComplete="off"
                className="h-9 w-full rounded border border-[#cbd5e1] bg-white px-3 text-sm text-[#111827] outline-none focus:border-[#0d73ff] focus:ring-2 focus:ring-[#b8d7ff]"
                placeholder={`Search ${label.toLowerCase()}`}
                ref={searchInputRef}
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          ) : null}
          <label className="flex cursor-pointer items-center justify-between gap-3 rounded px-2 py-2 text-sm font-semibold text-[#334155] hover:bg-[#eff6ff]">
            <span>Select all</span>
            <input
              checked={allSelected}
              className="h-4 w-4 flex-none accent-[#1773f8]"
              type="checkbox"
              onChange={() => onChange(allSelected ? [] : optionValues)}
            />
          </label>
          <div className="my-1 border-t border-[#e2e8f0]" />
          {visibleOptions.map((option) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            const optionIndex = options.findIndex(
              (candidate) => getOptionValue(candidate) === optionValue,
            );

            return (
              <label
                aria-selected={selectedValues.includes(optionValue)}
                className="flex cursor-pointer items-center justify-between gap-3 rounded px-2 py-2 text-sm text-[#1f2937] hover:bg-[#eff6ff]"
                key={optionValue}
                role="option"
              >
                <span className="min-w-0 flex-1">{optionLabel}</span>
                <input
                  checked={selectedValues.includes(optionValue)}
                  className="h-4 w-4 flex-none accent-[#1773f8]"
                  id={`${id}-option-${optionIndex}`}
                  type="checkbox"
                  onChange={() => toggleOption(optionValue)}
                />
              </label>
            );
          })}
          {visibleOptions.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-[#64748b]">
              No matching options found.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function DailyCallAssignmentPage() {
  usePageTitle("Daily Call Assignment | Pargro");
  const [metadata, setMetadata] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [metadataError, setMetadataError] = useState("");
  const [draftFilters, setDraftFilters] = useState(
    createEmptyDailyAssignmentFilters,
  );
  const [searchedFilters, setSearchedFilters] = useState(null);
  const [rows, setRows] = useState([]);
  const [targets, setTargets] = useState({});
  const [targetErrors, setTargetErrors] = useState({});
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [totalAvailablePool, setTotalAvailablePool] = useState(0);
  const [filteredPool, setFilteredPool] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [resultsStale, setResultsStale] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentError, setAssignmentError] = useState("");
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [message, setMessage] = useState(null);

  const loadMetadata = useCallback(async ({ preserveCurrent = false } = {}) => {
    if (!preserveCurrent) setMetadataLoading(true);
    setMetadataError("");

    try {
      const data = await fetchDailyAssignmentMetadata();
      const agents = (Array.isArray(data?.agents) ? data.agents : []).filter(
        (agent) => agent?.username,
      );
      const nextMetadata = { ...data, agents };

      setMetadata(nextMetadata);
      setTotalAvailablePool(Number(data?.total_available_pool) || 0);
      setSelectedAgentIds((current) =>
        current.filter((id) =>
          agents.some((agent) => getDailyAssignmentAgentId(agent) === id),
        ),
      );
      return nextMetadata;
    } catch (error) {
      setMetadataError(
        error.message || "Unable to load daily assignment metadata.",
      );
      return null;
    } finally {
      if (!preserveCurrent) setMetadataLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  const runSearch = useCallback(
    async (filters, { preserveMessage = false } = {}) => {
      const requestedFilters = cloneDailyAssignmentFilters(filters);
      setSearchLoading(true);
      setSearchError("");
      setResultsStale(true);
      if (!preserveMessage) {
        setMessage(null);
      }

      try {
        const data = await searchDailyAssignments(
          buildDailyAssignmentSearchPayload(requestedFilters),
        );
        const resultRows = Array.isArray(data?.rows) ? data.rows : [];
        const resultFilters = data?.filters
          ? cloneDailyAssignmentFilters(data.filters)
          : requestedFilters;

        setRows(resultRows);
        setSearchedFilters(resultFilters);
        setTargets(
          Object.fromEntries(
            resultRows.map((row) => [getDailyAssignmentRowSignature(row), 0]),
          ),
        );
        setTargetErrors({});
        setTotalAvailablePool(Number(data?.total_available_pool) || 0);
        setFilteredPool(Number(data?.filtered_pool) || 0);
        setResultsStale(false);
        return data;
      } catch (error) {
        setSearchError(
          error.message || "Unable to search the daily assignment pool.",
        );
        return null;
      } finally {
        setSearchLoading(false);
      }
    },
    [],
  );

  const filtersComplete = hasCompleteDailyAssignmentFilters(draftFilters);
  const selectedTarget = calculateSelectedTarget(targets);
  const selectedAgents = useMemo(
    () =>
      (metadata?.agents || []).filter((agent) =>
        selectedAgentIds.includes(getDailyAssignmentAgentId(agent)),
      ),
    [metadata, selectedAgentIds],
  );
  const selectedRowsCount = rows.filter(
    (row) => Number(targets[getDailyAssignmentRowSignature(row)]) > 0,
  ).length;
  const hasTargetErrors = Object.values(targetErrors).some(Boolean);
  const totalRemainingCapacity = selectedAgents.reduce(
    (total, agent) =>
      total + Math.max(0, Number(agent.remaining_capacity) || 0),
    0,
  );
  const capacityError =
    selectedAgents.length > 0 && selectedTarget > totalRemainingCapacity
      ? `Selected target exceeds the agents' combined remaining capacity of ${totalRemainingCapacity.toLocaleString("en-IN")}.`
      : "";
  const currentResultsAreStale =
    resultsStale ||
    (searchedFilters &&
      !areDailyAssignmentFiltersEqual(draftFilters, searchedFilters));

  const changeFilter = (key) => (selectedValues) => {
    setDraftFilters((current) => ({ ...current, [key]: selectedValues }));
    if (searchedFilters) setResultsStale(true);
    setSearchError("");
    setAssignmentError("");
    setMessage(null);
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    if (
      !filtersComplete ||
      metadataLoading ||
      searchLoading ||
      assignmentLoading
    )
      return;
    await runSearch(draftFilters);
  };

  const handleTargetChange = (row, rawValue) => {
    const normalizedValue = String(rawValue ?? "");

    if (normalizedValue && !/^\d+$/.test(normalizedValue)) {
      return;
    }

    const rowKey = getDailyAssignmentRowSignature(row);
    const validation = validateDailyAssignmentTarget(
      normalizedValue,
      row.available_leads,
    );

    if (validation.error) {
      return;
    }

    setTargets((current) => ({ ...current, [rowKey]: normalizedValue }));
    setTargetErrors((current) => ({ ...current, [rowKey]: validation.error }));
    setAssignmentError("");
    setMessage(null);
  };

  const handleAssign = () => {
    if (!searchedFilters || currentResultsAreStale) {
      setAssignmentError(
        "Filters changed after the last Search. Search again before assigning.",
      );
    } else if (selectedAgentIds.length === 0) {
      setAssignmentError("Please select at least one target agent.");
    } else if (selectedTarget === 0) {
      setAssignmentError("Enter a target number for at least one row.");
    } else if (hasTargetErrors) {
      setAssignmentError(
        "Correct the invalid target numbers before assigning.",
      );
    } else if (capacityError) {
      setAssignmentError(capacityError);
    } else {
      setAssignmentError("");
      setIsConfirmationOpen(true);
    }
  };

  const handleConfirmAssignment = async () => {
    const requestPayload = buildDailyAssignmentPayload({
      filters: searchedFilters,
      selectedAgents,
      rows,
      targets,
    });

    console.log("[Daily Call Assignment] API request payload:", requestPayload);
    setAssignmentLoading(true);
    setAssignmentError("");

    try {
      const result = await assignDailyAssignments(requestPayload);
      const successMessage =
        result?.message ||
        `${Number(result?.assigned ?? selectedTarget)} contacts assigned successfully.`;

      setMessage({ type: "success", text: successMessage });
      setIsConfirmationOpen(false);
      setDraftFilters(createEmptyDailyAssignmentFilters());
      setSearchedFilters(null);
      setSelectedAgentIds([]);
      setRows([]);
      setTargets({});
      setTargetErrors({});
      setFilteredPool(0);
      setResultsStale(false);
      setSearchError("");

      await loadMetadata({ preserveCurrent: true });
    } catch (error) {
      setAssignmentError(
        error.message || "Unable to assign the selected contacts.",
      );
    } finally {
      setAssignmentLoading(false);
    }
  };

  const agentOptionLabel = (agent) =>
    `${agent.name || agent.username} — assigned: ${Number(agent.assigned_today) || 0}`;
  const assignDisabled =
    assignmentLoading ||
    searchLoading ||
    !searchedFilters ||
    currentResultsAreStale ||
    selectedAgentIds.length === 0 ||
    selectedTarget === 0 ||
    hasTargetErrors ||
    Boolean(capacityError);

  return (
    <ActionPageLayout
      title="Daily Call Assignment"
      icon={
        <FaPhoneAlt
          className="h-6 w-6 flex-none rotate-[-18deg] text-[#0d73ff]"
          aria-hidden="true"
        />
      }
    >
      <section className="mt-6 flex h-[calc(100vh-110px)] min-h-0 w-full flex-col overflow-hidden max-[960px]:h-auto max-[960px]:overflow-visible">
        {metadataLoading ? (
          <div
            className="mb-4 flex items-center justify-center gap-2 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-4 py-4 text-sm text-[#1e40af]"
            role="status"
          >
            <FaSpinner className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading assignment filters and agents…
          </div>
        ) : null}
        {metadataError ? (
          <div
            className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
            role="alert"
          >
            <span>{metadataError}</span>
            <button
              className="rounded border border-[#842029] bg-white px-4 py-1.5 font-semibold hover:bg-[#fff5f5]"
              type="button"
              onClick={() => loadMetadata()}
            >
              Retry
            </button>
          </div>
        ) : null}

        <form
          className="shrink-0 rounded-lg border border-[#d9dfe8] bg-[#f8fafc] p-4 shadow-[0_2px_8px_rgba(15,23,42,0.1)]"
          onSubmit={handleSearch}
        >
          <div className="mb-4 flex items-center gap-2 text-lg font-bold text-[#172033]">
            <FaFilter className="h-4 w-4 text-[#6d4b8a]" aria-hidden="true" />
            Assignment filters
          </div>
          <div className="grid grid-cols-[repeat(4,minmax(10rem,1fr))_auto] items-end gap-4 max-[1100px]:grid-cols-2 max-[620px]:grid-cols-1">
            {filterFields.map(([key, label, placeholder]) => (
              <CheckboxDropdown
                disabled={!metadata || metadataLoading || assignmentLoading}
                id={`assignment-${key}`}
                key={key}
                label={label}
                onChange={changeFilter(key)}
                options={metadata?.[key] || []}
                placeholder={placeholder}
                searchable={key === "cluster_cities"}
                selectedValues={draftFilters[key]}
              />
            ))}
            <button
              className="inline-flex h-[40px] items-center justify-center gap-2 rounded-md bg-[#1773f8] px-6 text-sm font-semibold text-white transition hover:bg-[#0d63dc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff] disabled:cursor-not-allowed disabled:bg-[#94a3b8]"
              disabled={
                !metadata ||
                !filtersComplete ||
                searchLoading ||
                assignmentLoading
              }
              type="submit"
            >
              {searchLoading ? (
                <FaSpinner
                  className="h-3.5 w-3.5 animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <FaSearch className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {searchLoading ? "Searching…" : "Search"}
            </button>
          </div>
          {searchError ? (
            <p
              className="mb-0 mt-3 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-3 py-2 text-sm text-[#842029]"
              role="alert"
            >
              {searchError}
            </p>
          ) : null}
        </form>

        <section className="mt-4 grid shrink-0 grid-cols-[repeat(3,minmax(10rem,1fr))_minmax(18rem,1.4fr)_auto] items-end gap-4 rounded-lg border border-[#d9dfe8] bg-white p-4 shadow-[0_2px_8px_rgba(15,23,42,0.08)] max-[1100px]:grid-cols-2 max-[620px]:grid-cols-1">
          <Metric label="Total available pool" value={totalAvailablePool} />
          <Metric label="Filtered pool" value={filteredPool} />
          <Metric accent label="Selected target" value={selectedTarget} />
          <CheckboxDropdown
            disabled={!metadata || metadataLoading || assignmentLoading}
            getOptionLabel={agentOptionLabel}
            getOptionValue={getDailyAssignmentAgentId}
            id="target-agent"
            label="Target Agent"
            onChange={(values) => {
              setSelectedAgentIds(values);
              setAssignmentError("");
              setMessage(null);
            }}
            options={metadata?.agents || []}
            placeholder="Select agents"
            selectedValues={selectedAgentIds}
          />
          <button
            className="inline-flex h-[40px] items-center justify-center gap-2 rounded-md bg-[#63c832] px-7 text-sm font-bold text-[#102006] shadow-sm transition hover:bg-[#50ad25] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a8e58c] disabled:cursor-not-allowed disabled:bg-[#a7cf94]"
            disabled={assignDisabled}
            type="button"
            onClick={handleAssign}
          >
            {assignmentLoading ? (
              <FaSpinner className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <FaUsers className="h-4 w-4" aria-hidden="true" />
            )}
            {assignmentLoading ? "Assigning…" : "Assign"}
          </button>
        </section>

        {capacityError ? <Alert type="warning" text={capacityError} /> : null}
        {currentResultsAreStale && searchedFilters ? (
          <Alert
            type="warning"
            text="Filters changed after the last Search. Search again to refresh the assignment matrix."
          />
        ) : null}
        {assignmentError && !isConfirmationOpen ? (
          <Alert type="error" text={assignmentError} />
        ) : null}
        {message ? <Alert type={message.type} text={message.text} /> : null}

        <div className="mt-4 min-h-0 flex-[0_1_auto] overflow-auto rounded-lg border border-[#cbd5e1] bg-white shadow-[0_2px_8px_rgba(15,23,42,0.08)] max-[960px]:max-h-[60vh]">
          <table className="w-full min-w-[920px] border-collapse text-sm">
            <thead>
              <tr>
                {columns.map(([label, key, alignment]) => (
                  <th
                    className={`sticky top-0 z-10 border-b border-r border-[#cbd5e1] bg-[#eaf3fa] px-3 py-3 font-bold text-[#172033] last:border-r-0 ${alignment}`}
                    key={key}
                    scope="col"
                  >
                    {label}
                  </th>
                ))}
                <th
                  className="sticky top-0 z-10 border-b border-[#cbd5e1] bg-[#7ec8e3] px-3 py-3 text-center font-bold text-[#172033]"
                  scope="col"
                >
                  Target number
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const rowKey = getDailyAssignmentRowSignature(row);
                const targetValue = targets[rowKey] ?? 0;
                const hasTarget =
                  Number(targetValue) > 0 && !targetErrors[rowKey];
                return (
                  <tr
                    className={`${hasTarget ? "bg-[#fffed1]" : index % 2 === 0 ? "bg-white" : "bg-[#f8fafc]"} transition-colors`}
                    key={rowKey}
                  >
                    {columns.map(([, key, alignment]) => (
                      <td
                        className={`border-b border-r border-[#d9dfe8] px-3 py-2.5 text-[#1f2937] ${alignment}`}
                        key={key}
                      >
                        {row[key]}
                      </td>
                    ))}
                    <td className="border-b border-[#d9dfe8] bg-[#eef9fd] px-3 py-1.5 text-center">
                      <input
                        aria-describedby={
                          targetErrors[rowKey]
                            ? `target-error-${index}`
                            : undefined
                        }
                        aria-invalid={Boolean(targetErrors[rowKey])}
                        aria-label={`Target number for ${row.call_banding}, row ${index + 1}`}
                        className="h-[34px] w-24 rounded border border-[#93c5d8] bg-white px-2 text-center font-semibold outline-none focus:border-[#0d73ff] focus:ring-2 focus:ring-[#b8d7ff] aria-[invalid=true]:border-[#dc3545]"
                        disabled={currentResultsAreStale || assignmentLoading}
                        inputMode="numeric"
                        type="text"
                        value={targetValue}
                        onChange={(event) =>
                          handleTargetChange(row, event.target.value)
                        }
                      />
                      {targetErrors[rowKey] ? (
                        <div
                          className="mt-1 text-xs text-[#b42318]"
                          id={`target-error-${index}`}
                        >
                          {targetErrors[rowKey]}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
              {!searchLoading && rows.length === 0 ? (
                <tr>
                  <td
                    className="px-4 py-10 text-center text-[#64748b]"
                    colSpan={columns.length + 1}
                  >
                    {searchedFilters
                      ? "No contacts match the selected filters."
                      : "Select all assignment filters and click Search to view assignment data."}
                  </td>
                </tr>
              ) : null}
              {searchLoading && rows.length === 0 ? (
                <tr>
                  <td
                    className="px-4 py-10 text-center text-[#64748b]"
                    colSpan={columns.length + 1}
                  >
                    Loading assignment matrix…
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {isConfirmationOpen ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0f172a]/55 p-4"
          role="presentation"
        >
          <section
            aria-labelledby="assignment-confirmation-title"
            aria-modal="true"
            className="w-full max-w-lg overflow-hidden rounded-lg bg-white shadow-[0_24px_60px_rgba(15,23,42,0.35)]"
            role="dialog"
          >
            <header className="flex items-center justify-between border-b border-[#d9dfe8] px-5 py-4">
              <h2
                className="m-0 text-lg font-bold text-[#172033]"
                id="assignment-confirmation-title"
              >
                Confirm Call Assignment
              </h2>
              <button
                aria-label="Close confirmation"
                className="inline-flex h-8 w-8 items-center justify-center rounded text-[#64748b] hover:bg-[#f1f5f9] disabled:cursor-not-allowed"
                disabled={assignmentLoading}
                type="button"
                onClick={() => setIsConfirmationOpen(false)}
              >
                <FaTimes className="h-4 w-4" aria-hidden="true" />
              </button>
            </header>
            <div className="grid gap-3 p-5 text-sm text-[#334155]">
              <div>
                <span className="font-bold">Selected agents:</span>{" "}
                {selectedAgents
                  .map((agent) => agent.name || agent.username)
                  .join(", ")}
              </div>
              <div>
                <span className="font-bold">Selected target:</span>{" "}
                {selectedTarget.toLocaleString("en-IN")}
              </div>
              <div>
                <span className="font-bold">Selected matrix rows:</span>{" "}
                {selectedRowsCount.toLocaleString("en-IN")}
              </div>
              <div>
                <span className="font-bold">Campaign:</span>{" "}
                {metadata?.assignment_rules?.campaign_name || "—"}
              </div>
              {assignmentError ? (
                <Alert type="error" text={assignmentError} compact />
              ) : null}
            </div>
            <footer className="flex justify-end gap-3 border-t border-[#d9dfe8] bg-[#f8fafc] px-5 py-4">
              <button
                className="h-10 rounded-md border border-[#cbd5e1] bg-white px-5 text-sm font-semibold text-[#334155] hover:bg-[#f1f5f9] disabled:cursor-not-allowed"
                disabled={assignmentLoading}
                type="button"
                onClick={() => setIsConfirmationOpen(false)}
              >
                Cancel
              </button>
              <button
                className="inline-flex h-10 items-center gap-2 rounded-md bg-[#63c832] px-5 text-sm font-bold text-[#102006] hover:bg-[#50ad25] disabled:cursor-not-allowed disabled:bg-[#a7cf94]"
                disabled={assignmentLoading}
                type="button"
                onClick={handleConfirmAssignment}
              >
                {assignmentLoading ? (
                  <FaSpinner
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <FaUsers className="h-4 w-4" aria-hidden="true" />
                )}
                {assignmentLoading ? "Assigning…" : "Confirm Assignment"}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </ActionPageLayout>
  );
}

function Alert({ type, text, compact = false }) {
  const styles = {
    success: "border-[#badbcc] bg-[#d1e7dd] text-[#0f5132]",
    error: "border-[#f5c2c7] bg-[#f8d7da] text-[#842029]",
    warning: "border-[#ffe69c] bg-[#fff3cd] text-[#664d03]",
  };
  return (
    <div
      className={`${compact ? "" : "mt-4"} flex items-center gap-2 rounded-md border px-4 py-3 text-sm ${styles[type]}`}
      role={type === "error" ? "alert" : "status"}
    >
      {type === "success" ? (
        <FaCheckCircle className="h-4 w-4 flex-none" aria-hidden="true" />
      ) : null}
      {type === "warning" ? (
        <FaExclamationTriangle
          className="h-4 w-4 flex-none"
          aria-hidden="true"
        />
      ) : null}
      {text}
    </div>
  );
}

function Metric({ accent = false, label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-[#64748b]">
        {label}
      </div>
      <div
        className={`mt-1 text-2xl font-bold ${accent ? "text-[#0284c7]" : "text-[#172033]"}`}
      >
        {Number(value || 0).toLocaleString("en-IN")}
      </div>
    </div>
  );
}

export default DailyCallAssignmentPage;
